import type { Permission } from '@/client/types.gen'

export interface MembershipRightsRow {
	level: string
	resource_id: string
	permissions: string[]
}

export interface PermissionScope {
	projectId?: string
	workspaceId?: string | null
	companyId?: string
}

export function hasPermission(
	memberships: MembershipRightsRow[] | undefined,
	permission: Permission | string,
	scope?: PermissionScope
): boolean {
	if (!memberships?.length) return false

	const { projectId, workspaceId, companyId } = scope ?? {}

	if (projectId) {
		const projectRow = memberships.find((m) => m.level === 'project' && m.resource_id === projectId)
		if (projectRow?.permissions?.includes(permission)) return true
	}
	if (workspaceId) {
		const workspaceRow = memberships.find(
			(m) => m.level === 'workspace' && m.resource_id === workspaceId
		)
		if (workspaceRow?.permissions?.includes(permission)) return true
	}
	if (companyId) {
		const companyRow = memberships.find((m) => m.level === 'company' && m.resource_id === companyId)
		if (companyRow?.permissions?.includes(permission)) return true
	}
	return false
}

export function userCanProjectEdit(
	memberships: MembershipRightsRow[] | undefined,
	projectId: string,
	workspaceId?: string | null
): boolean {
	return hasPermission(memberships, 'project:edit', { projectId, workspaceId })
}

export function userCanDeleteProject(
	memberships: MembershipRightsRow[] | undefined,
	projectId: string,
	workspaceId?: string | null
): boolean {
	return hasPermission(memberships, 'project:delete', { projectId, workspaceId })
}

export function userCanCreateProject(
	memberships: MembershipRightsRow[] | undefined,
	workspaceId: string
): boolean {
	return hasPermission(memberships, 'project:create', { workspaceId })
}

export function userCanDeleteAnalysis(
	memberships: MembershipRightsRow[] | undefined,
	projectId: string,
	workspaceId?: string | null
): boolean {
	return hasPermission(memberships, 'analysis:delete', { projectId, workspaceId })
}

export function userCanDeleteArtifact(
	memberships: MembershipRightsRow[] | undefined,
	projectId: string,
	workspaceId?: string | null
): boolean {
	return hasPermission(memberships, 'artifact:delete', { projectId, workspaceId })
}

export function userCanWorkspaceAddMembers(
	memberships: MembershipRightsRow[] | undefined,
	workspaceId: string
): boolean {
	return hasPermission(memberships, 'workspace:add_members', { workspaceId })
}

export function userCanWorkspaceEditMembers(
	memberships: MembershipRightsRow[] | undefined,
	workspaceId: string
): boolean {
	return hasPermission(memberships, 'workspace:edit_members', { workspaceId })
}

export function userCanWorkspaceRemoveMembers(
	memberships: MembershipRightsRow[] | undefined,
	workspaceId: string
): boolean {
	return hasPermission(memberships, 'workspace:remove_members', { workspaceId })
}
